// --------------------------------------------------------------
// Created On: 2023-10-10
// Author: Zachary Thomas
//
// Last Modified: 2025-10-27
// Modified By: Zachary Thomas
//
// Copyright 2024 - 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React from "react";
import formatDateShortLocal from "../../../utilities/time/formatDateShortLocal";
import styles from "./OperationLogList.module.scss";

// A list of operation logs for the current asset.
export default function OperationLogList(props: Props): Component {
  // Format the operation message.
  function formatOperationMessage(
    username: string,
    emailAddress: string,
    commandType: string,
    value: string,
    operationUtc: string,
    commandSucceeded: boolean
  ): string {
    const localTimestamp = formatDateShortLocal(operationUtc);
    switch (commandType.toUpperCase()) {
      case "DEVICE STATUS": {
        let action = "UNKNOWN";
        switch (value.toUpperCase()) {
          case "CLEAR MESSAGES":
            action = "clear messages on the monitoring device";
            break;
          case "RESET":
            action = "restart the monitoring device";
            break;
          case "REAPPLY CONFIGURATION":
            action = "reapply configuration";
            break;
          case "BLINK LEDS":
            action = "blink LEDs on the monitoring device";
            break;
          default:
            break;
        }
        return `${username} (${emailAddress}) ${
          commandSucceeded ? "sent command" : "attempted/failed"
        } to ${action} on ${localTimestamp}.`;
      }
      case "FIRMWARE UPDATE": {
        return `${username} (${emailAddress}) ${
          commandSucceeded ? "successfully updated firmware" : "initiated firmware update"
        } on ${localTimestamp}.`;
      }
      case "VALVE POSITION": {
        let valveNumber = "?";
        let percentOpen = "?";
        const valueSplit = value.split("|");
        if (valueSplit.length === 2) {
          valveNumber = valueSplit[0];
          percentOpen = valueSplit[1];
        }
        return `${username} (${emailAddress}) ${
          commandSucceeded ? "sent command" : "attempted/failed"
        } to set valve #${valveNumber} to ${percentOpen}% open on ${localTimestamp}.`;
      }
      default:
        return `${username} (${emailAddress}) ${
          commandSucceeded ? "" : "attempted/failed"
        } set ${commandType} to '${value}' on ${localTimestamp}.`;
    }
  }

  return (
    <div className="mx-3">
      <div className={styles.titleBar}>
        <label className={styles.title}>{props.assetName} Operation Logs</label>
      </div>
      <div className={styles.body}>
        {props.operationLogs.map((operationLog) => (
          <div key={operationLog.assetOperationLogId} className={styles.item}>
            {formatOperationMessage(
              operationLog.username,
              operationLog.emailAddress,
              operationLog.commandType,
              operationLog.value,
              operationLog.operationUtc,
              operationLog.commandSucceeded
            )}
          </div>
        ))}

        {props.operationLogs.length === 0 && (
          <div className={styles.item}>No operation logs created for this asset.</div>
        )}
      </div>
    </div>
  );
}

interface Props {
  assetName: string;
  operationLogs: OperationLog[];
}

interface OperationLog {
  assetOperationLogId: number;
  commandType: string;
  value: string;
  commandSucceeded: boolean;
  username: string;
  emailAddress: string;
  operationUtc: string;
}
